<template>
  <div v-if="isPermissionDenied" class="permission-denied">
    <div class="permission-denied-content">
      <h2>{{ $t('common.no_permission') }}</h2>
      <p>{{ $t('billing_reports.no_permission_message') }}</p>
    </div>
  </div>
  <div v-else class="billingReportContainer">
    <!-- Loading Spinner - Transparent Overlay -->
    <div v-if="loading" class="loading-overlay">
      <div class="spinner-border text-primary spinner-grow-xl" role="status"></div>
    </div>

    <!-- Filters and Content -->
    <div class="billingContent">
      <!-- Filter Controls -->
      <div class="billingFiltersContainer">
        <div class="filterRow">
          <div class="filterGroup">
            <label class="filterLabel">{{ $t('billing.fromDate') }}</label>
            <input type="date" class="filterInput" v-model="filters.fromDate" @change="handleFilterChange" />
          </div>
          <div class="filterGroup">
            <label class="filterLabel">{{ $t('billing.toDate') }}</label>
            <input type="date" class="filterInput" v-model="filters.toDate" @change="handleFilterChange" />
          </div>
          <div class="filterGroup">
            <!-- <label class="filterLabel">{{ $t('billing.tenantIds') }}</label> -->
            <div class="custom-multi-select">
              <div class="multi-select-input" @click="toggleTenantDropdown" :class="{ open: showTenantDropdown }">
                <div class="selected-tenants">
                  <span v-if="selectedTenants.length === 0" class="placeholder">{{ isSuperAdmin ? $t('billing.allTenants') : $t('billing.tenantIdsPlaceholder') }}</span>
                  <div v-else class="tenant-tags">
                    <span v-for="tenant in selectedTenants" :key="tenant.id" class="tenant-tag">
                      {{ tenant.tenant_name }}
                      <button type="button" class="remove-tenant" @click.stop="removeTenant(tenant.id)">
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                          <path d="M9 3L3 9M3 3L9 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                      </button>
                    </span>
                  </div>
                </div>
                <svg class="dropdown-arrow" :class="{ rotated: showTenantDropdown }" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M6 9L12 15L18 9" stroke="#5C656C" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </div>
              <div v-if="showTenantDropdown" class="tenant-dropdown">
                <div class="search-container">
                  <input type="text" v-model="tenantSearchQuery" :placeholder="$t('common.search') || 'Search tenants...'" class="search-input" @click.stop @input="filterTenants" />
                </div>
                <div class="tenant-options">
                  <div v-for="tenant in filteredTenants" :key="tenant.id" class="tenant-option" :class="{ selected: isTenantSelected(tenant.id) }" @click="toggleTenant(tenant)">
                    <div class="option-checkbox">
                      <svg v-if="isTenantSelected(tenant.id)" width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path d="M10 3L4.5 8.5L2 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                      </svg>
                    </div>
                    <span class="tenant-name">{{ tenant.tenant_name }}</span>
                  </div>
                  <div v-if="filteredTenants.length === 0" class="no-tenants">
                    {{ $t('common.no_options') || 'No tenants found' }}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="filterButtonGroup">
            <button class="resetDateButton" @click="handleSearch">{{ $t('reports.generateReport') }}</button>
            <button class="resetDateButton" @click="resetAllFilters">{{ $t('reports.reset') }}</button>
          </div>
        </div>
      </div>

      <!-- Summary Section -->
      <div v-if="summaryData && !loading" class="summaryContainer">
        <div class="summaryTitle">{{ $t('billing.summary') }}</div>
        <div class="summaryGrid">
          <div class="summaryItem">
            <span class="summaryLabel">{{ $t('billing.totalJobs') }}</span>
            <span class="summaryValue">{{ summaryData.total_jobs || 0 }}</span>
          </div>
          <div class="summaryItem">
            <span class="summaryLabel">{{ $t('billing.totalDocuments') }}</span>
            <span class="summaryValue">{{ summaryData.total_documents || 0 }}</span>
          </div>
          <div class="summaryItem">
            <span class="summaryLabel">{{ $t('billing.totalFiles') }}</span>
            <span class="summaryValue">{{ summaryData.total_files || 0 }}</span>
          </div>
          <div class="summaryItem">
            <span class="summaryLabel">{{ $t('billing.totalPages') }}</span>
            <span class="summaryValue">{{ summaryData.total_pages || 0 }}</span>
          </div>
        </div>
      </div>

      <!-- Pagination Controls -->
      <div class="tableHeadingsNew">
        <div class="customRow">
          <!-- Download Buttons -->
          <button :disabled="downloadingPDF" class="downloadExcelButton" @click="downloadAsPDF">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
              <polyline points="14,2 14,8 20,8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
              <line x1="10" y1="9" x2="8" y2="9" />
            </svg>
            {{ downloadingPDF ? $t('reports.downloading') : $t('reports.download_pdf') }}
          </button>
          <button class="downloadExcelButton" @click="downloadAsExcel">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
              <polyline points="14,2 14,8 20,8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="10" y1="9" x2="8" y2="9" />
            </svg>
            {{ downloadingExcel ? $t('reports.downloading') : $t('reports.download_excel') }}
          </button>
        </div>
      </div>

      <!-- Billing Table -->
      <div class="tableContainer" style="margin-top: 16px">
        <table class="customTable">
          <thead class="customTableHeader">
            <tr>
              <th class="col-tenant-id" :title="$t('billing.tableHeaders.tenantId')">
                {{ $t('billing.tableHeaders.tenantId') }}
              </th>
              <th class="col-tenant-name" :title="$t('billing.tableHeaders.tenantName')">
                {{ $t('billing.tableHeaders.tenantName') }}
              </th>
              <th class="col-ai-method" :title="$t('billing.tableHeaders.aiMethod')">
                {{ $t('billing.tableHeaders.aiMethod') }}
              </th>
              <th class="col-jobs-processed" :title="$t('billing.tableHeaders.jobsProcessed')">
                {{ $t('billing.tableHeaders.jobsProcessed') }}
              </th>
              <th class="col-documents-processed" :title="$t('billing.tableHeaders.documentsProcessed')">
                {{ $t('billing.tableHeaders.documentsProcessed') }}
              </th>
              <th class="col-files-processed" :title="$t('billing.tableHeaders.filesProcessed')">
                {{ $t('billing.tableHeaders.filesProcessed') }}
              </th>
              <th class="col-pages-processed" :title="$t('billing.tableHeaders.pagesProcessed')">
                {{ $t('billing.tableHeaders.pagesProcessed') }}
              </th>
              <th class="col-pages-licensed" :title="$t('billing.tableHeaders.pagesLicensed')">
                {{ $t('billing.tableHeaders.pagesLicensed') }}
              </th>
              <th class="col-subscription-date" :title="$t('billing.tableHeaders.subscriptionDate')">
                {{ $t('billing.tableHeaders.subscriptionDate') }}
              </th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="billing in billingData" :key="billing.tenant_id" class="tableRow">
              <td class="col-tenant-id" :title="billing.tenant_id">
                {{ billing.tenant_id }}
              </td>
              <td class="col-tenant-name word-wrap" :title="billing.tenant_name">
                {{ billing.tenant_name }}
              </td>
              <td class="col-ai-method word-wrap" :title="billing.ai_method">
                {{ billing.ai_method }}
              </td>
              <td class="col-jobs-processed" :title="billing.jobs_processed">
                {{ billing.jobs_processed }}
              </td>
              <td class="col-documents-processed" :title="billing.documents_processed">
                {{ billing.documents_processed }}
              </td>
              <td class="col-files-processed" :title="billing.files_processed">
                {{ billing.files_processed }}
              </td>
              <td class="col-pages-processed" :title="billing.pages_processed">
                {{ billing.pages_processed }}
              </td>
              <td class="col-pages-licensed" :title="billing.pages_licensed">
                {{ billing.pages_licensed }}
              </td>
              <td class="col-subscription-date word-wrap" :title="formatDate(billing.subscription_start_date)">
                {{ formatDate(billing.subscription_start_date) }}
              </td>
            </tr>
            <tr v-if="billingData.length === 0" class="no-data-row">
              <td colspan="9" class="no-data-message">
                <div class="no-data-container">
                  <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--primary-color)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                  </svg>
                  <h3>{{ $t('billing.noDataAvailable') }}</h3>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { get } from '@/utils/api'
import { showToast } from '../../utils/toast.js'

export default {
  name: 'BillingReport',
  components: {},
  data() {
    return {
      loading: false,
      billingData: [],
      readPermission: false,
      writePermission: false,
      isPermissionDenied: false,
      summaryData: null,
      filters: {
        fromDate: '',
        toDate: '',
        tenantIds: [],
      },
      tenantOptions: [],
      page: 1,
      pageSize: 100,
      totalPages: 1,
      total: 0,
      pageSizeOptions: [10, 20, 50, 100],
      showPageSizeDropdown: false,
      downloadingPDF: false,
      downloadingExcel: false,
      // Multi-select tenant functionality
      tenantSearchQuery: '',
      showTenantDropdown: false,
      selectedTenants: [],
    }
  },
  mounted() {
    this.checkPermissions()
    if (!this.isPermissionDenied) {
      this.fetchTenants()
      this.fetchBilling()
      // Initialize selectedTenants from existing tenantIds if any
      this.initializeSelectedTenants()
    }
    document.addEventListener('click', this.handleClickAway)
  },
  created() {
    // Get user data from localStorage
    const userDataString = localStorage.getItem('UserData')
    if (userDataString) {
      this.userData = JSON.parse(userDataString)
    }
    // Check if user is tenant admin and set default tenant
    this.checkAndSetDefaultTenant()
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickAway)
  },
  computed: {
    filteredTenants() {
      if (!this.tenantSearchQuery) return this.tenantOptions
      return this.tenantOptions.filter((tenant) => tenant.tenant_name.toLowerCase().includes(this.tenantSearchQuery.toLowerCase()))
    },
    isSuperAdmin() {
      const userRolesString = localStorage.getItem('UserRoles')
      if (userRolesString) {
        try {
          const userRoles = JSON.parse(userRolesString)
          if (userRoles.length > 0) {
            const roleId = userRoles[0].roleId
            // Super Admin has roleId === 2
            return roleId === 2
          }
        } catch (err) {
          console.error('Error parsing user roles:', err)
        }
      }
      return false
    },
  },
  methods: {
    checkPermissions() {
      const permissions = JSON.parse(localStorage.getItem('permissions') || '[]')
      this.readPermission = permissions.some((p) => p.object_name === 'BILLING_REPORT_RD' && p.is_active)
      this.writePermission = permissions.some((p) => p.object_name === 'BILLING_REPORT_WR' && p.is_active)
      if (!this.readPermission) {
        this.isPermissionDenied = true
        return
      }

      this.isPermissionDenied = false
    },

    async fetchTenants() {
      try {
        const res = await get('/v1/tenants?page=1&pageSize=10000')

        console.log('Tenant API Response:', res.data)

        // Handle the specific API response structure
        if (res?.data?.code === 200 && res?.data?.data?.tenants) {
          const tenants = res.data.data.tenants

          this.tenantOptions = tenants.map((tenant) => ({
            id: tenant.id,
            tenant_name: tenant.tenant_name,
          }))

          console.log('Processed tenantOptions:', this.tenantOptions)

          // Initialize selected tenants after fetching
          this.initializeSelectedTenants()
        } else {
          console.log('No tenants found in response')
          this.tenantOptions = []
        }
      } catch (err) {
        console.error('Failed to fetch tenants:', err)
        showToast({
          title: this.$t('reports.toast.error'),
          message: 'Failed to load tenant options',
          type: 'error',
        })
      }
    },

    async fetchBilling() {
      this.loading = true
      try {
        const params = {
          page: this.page,
          pageSize: this.pageSize,
          ...this.filters,
        }

        // Remove empty tenantIds
        if (!params.tenantIds || params.tenantIds.length === 0) {
          delete params.tenantIds
        }

        // Remove empty filter values
        Object.keys(params).forEach((key) => {
          if (params[key] === '' || params[key] === null || params[key] === undefined) {
            delete params[key]
          }
        })

        const queryString = new URLSearchParams(params).toString()
        const res = await get(`/v1/reports/billing?${queryString}`)

        if (res?.data) {
          this.billingData = res.data.data || []
          this.summaryData = res.data.summary || null
          this.total = res.data.totalCount || 0
          this.totalPages = Math.ceil(this.total / this.pageSize)
        }
      } catch (err) {
        this.billingData = []
        this.summaryData = null
        this.total = 0
        this.totalPages = 1
        showToast({
          title: this.$t('reports.toast.error'),
          message: err.response?.data?.message || this.$t('reports.toast.failed_to_load'),
          type: 'error',
        })
      } finally {
        this.loading = false
      }
    },

    handleFilterChange() {
      // Debounced filter change can be implemented here if needed
    },

    handleClickAway(event) {
      const dropdown = this.$refs.dropdownEl
      if (dropdown && !dropdown.contains(event.target)) {
        this.showPageSizeDropdown = false
      }
      // Close tenant dropdown when clicking outside
      const tenantDropdown = this.$el.querySelector('.custom-multi-select')
      if (tenantDropdown && !tenantDropdown.contains(event.target)) {
        this.showTenantDropdown = false
      }
    },

    // Multi-select tenant methods
    toggleTenantDropdown() {
      this.showTenantDropdown = !this.showTenantDropdown
      if (this.showTenantDropdown) {
        this.tenantSearchQuery = ''
      }
    },

    toggleTenant(tenant) {
      const index = this.selectedTenants.findIndex((t) => t.id === tenant.id)
      if (index > -1) {
        this.selectedTenants.splice(index, 1)
      } else {
        this.selectedTenants.push(tenant)
      }
      this.updateTenantIds()
    },

    removeTenant(tenantId) {
      this.selectedTenants = this.selectedTenants.filter((t) => t.id !== tenantId)
      this.updateTenantIds()
    },

    isTenantSelected(tenantId) {
      return this.selectedTenants.some((t) => t.id === tenantId)
    },

    updateTenantIds() {
      this.filters.tenantIds = this.selectedTenants.map((t) => t.id)
    },

    filterTenants() {
      // This method is called by the @input event, but filtering is handled by computed property
    },

    initializeSelectedTenants() {
      // This will be called after tenants are fetched to initialize selectedTenants
      if (this.filters.tenantIds && this.filters.tenantIds.length > 0 && this.tenantOptions.length > 0) {
        this.selectedTenants = this.tenantOptions.filter((tenant) => this.filters.tenantIds.includes(tenant.id))
      }
    },

    checkAndSetDefaultTenant() {
      // Check if user is tenant admin and automatically select their tenant
      const userRolesString = localStorage.getItem('UserRoles')
      if (userRolesString && this.userData && this.userData.tenant_id) {
        try {
          const userRoles = JSON.parse(userRolesString)
          if (userRoles.length > 0) {
            const roleId = userRoles[0].roleId
            // For Tenant Admin (roleId === 1), auto-select their tenant
            if (roleId === 1) {
              this.filters.tenantIds = [this.userData.tenant_id]
            }
          }
        } catch (err) {
          console.error('Error parsing user roles:', err)
        }
      }
    },

    togglePageSizeDropdown() {
      this.showPageSizeDropdown = !this.showPageSizeDropdown
    },

    selectPageSize(size) {
      this.pageSize = size
      this.page = 1
      this.showPageSizeDropdown = false
      this.fetchBilling()
    },

    goToPreviousPage() {
      if (this.page > 1) {
        this.page--
        this.fetchBilling()
      }
    },

    goToNextPage() {
      if (this.page < this.totalPages) {
        this.page++
        this.fetchBilling()
      }
    },

    handleSearch() {
      this.page = 1
      this.fetchBilling()
    },

    resetAllFilters() {
      this.filters = {
        fromDate: '',
        toDate: '',
        tenantIds: [],
      }
      this.selectedTenants = []
      this.tenantSearchQuery = ''
      this.showTenantDropdown = false
      this.page = 1

      // Re-apply default tenant selection for tenant admin
      this.checkAndSetDefaultTenant()
      this.initializeSelectedTenants()

      this.fetchBilling()
    },

    formatDate(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },

    async downloadAsPDF() {
      // if (!this.writePermission) {
      //   showToast({
      //     title: this.$t('common.no_permission'),
      //     message: this.$t('reports.no_write_permission'),
      //     type: 'error',
      //   })
      //   return
      // }

      this.downloadingPDF = true
      try {
        // Prepare params for fetching all data
        const params = {
          page: 1,
          pageSize: 1000000,
          ...this.filters,
        }

        // Remove empty tenantIds
        if (!params.tenantIds || params.tenantIds.length === 0) {
          delete params.tenantIds
        }

        // Remove empty filter values
        Object.keys(params).forEach((key) => {
          if (params[key] === '' || params[key] === null || params[key] === undefined) {
            delete params[key]
          }
        })

        const queryString = new URLSearchParams(params).toString()
        const res = await get(`/v1/reports/billing?${queryString}`)

        let allBillingData = []
        let summaryData = null
        if (res?.data) {
          allBillingData = res.data.data || []
          summaryData = res.data.summary || null
        }

        if (allBillingData.length === 0) {
          showToast({
            title: this.$t('reports.toast.no_data'),
            message: this.$t('reports.toast.no_billing_data'),
            type: 'warning',
          })
          return
        }

        // Generate PDF content
        this.generatePDF(allBillingData, summaryData)
      } catch (error) {
        showToast({
          title: this.$t('reports.toast.error'),
          message: error.response?.data?.message || this.$t('reports.toast.failed_download'),
          type: 'error',
        })
      } finally {
        this.downloadingPDF = false
      }
    },

    generatePDF(billingData, summaryData) {
      const logoBase64 = '/footer-logo_vt.png'

      const htmlContent = `
    <html>
      <head>
        <title>Billing Report</title>
        <style>
          body { 
            font-family: Arial, sans-serif; 
            margin: 20px; 
            font-size: 12px;
          }
          .report-header {
            display: flex;
            flex-direction: column;
            align-items: center;
            margin-bottom: 20px;
          }
          .header-top {
            display: flex;
            align-items: center;
            gap: 10px;
          }
          .header-top img {
            height: 30px;
          }
          .header-title {
            color: #164863; 
            font-size: 20px;
            font-weight: bold;
          }
          .header-bottom {
            margin-top: 5px;
            font-size: 10px;
            color: #666;
            text-align: center;
          }
          .summary-section {
            background-color: #f8f9fa;
            padding: 15px;
            border-radius: 5px;
            margin-bottom: 20px;
            border-left: 4px solid #164863;
            display: flex;
            flex-direction: row;
            flex-wrap: nowrap;
            justify-content: space-between;
            gap: 15px;
            margin-bottom: 10px;
          }
          .summary-title {
            margin: 0 0 10px 0;
            color: #164863;
            font-size: 16px;
            font-weight: bold;
          }
          .summary-grid {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 15px;
            margin-bottom: 10px;
          }
          .summary-item {
            text-align: center;
          }
          .summary-label {
            display: block;
            font-size: 10px;
            color: #666;
            margin-bottom: 5px;
          }
          .summary-value {
            display: block;
            font-size: 18px;
            font-weight: bold;
            color: #164863;
          }
          .period-info {
            text-align: center;
            font-size: 12px;
            color: #666;
            font-weight: bold;
          }
          table { 
            width: 100%; 
            border-collapse: collapse; 
            margin-top: 20px; 
            font-size: 10px;
          }
          th, td { 
            border: 1px solid #ddd; 
            padding: 8px; 
            text-align: left; 
            word-wrap: break-word;
          }
          th { 
            background-color: #f8f9fa; 
            font-weight: bold; 
            color: #164863; 
            font-size: 11px;
          }
          tr:nth-child(even) { 
            background-color: #f9f9f9; 
          }
        </style>
      </head>
      <body>
        <div class="report-header">
          <div class="header-top">
            <img src="${logoBase64}" alt="Logo" />
            <div class="header-title">Billing Report</div>
          </div>
          <div class="header-bottom">
            <p>Generated on: ${new Date().toLocaleString()}</p>
            <p>Exported by: ${this.userData?.email_id || '---'}</p>
            <p>Total Records: ${billingData.length}</p>
          </div>
        </div>

        ${this.generateSummarySection(summaryData)}
        
        <table>
          <thead>
            <tr>
              <th>${this.$t('billing.pdfHeaders.tenantId')}</th>
              <th>${this.$t('billing.pdfHeaders.tenantName')}</th>
              <th>${this.$t('billing.pdfHeaders.aiMethod')}</th>
              <th>${this.$t('billing.pdfHeaders.jobsProcessed')}</th>
              <th>${this.$t('billing.pdfHeaders.documentsProcessed')}</th>
              <th>${this.$t('billing.pdfHeaders.filesProcessed')}</th>
              <th>${this.$t('billing.pdfHeaders.pagesProcessed')}</th>
              <th>${this.$t('billing.pdfHeaders.pagesLicensed')}</th>
              <th>${this.$t('billing.pdfHeaders.subscriptionDate')}</th>
            </tr>
          </thead>
          <tbody>
            ${billingData
              .map(
                (billing) => `
              <tr>
                <td>${billing.tenant_id || '---'}</td>
                <td>${billing.tenant_name || '---'}</td>
                <td>${billing.ai_method || '---'}</td>
                <td>${billing.jobs_processed || 0}</td>
                <td>${billing.documents_processed || 0}</td>
                <td>${billing.files_processed || 0}</td>
                <td>${billing.pages_processed || 0}</td>
                <td>${billing.pages_licensed || 0}</td>
                <td>${this.formatDate(billing.subscription_start_date)}</td>
              </tr>
            `
              )
              .join('')}
          </tbody>
        </table>
      </body>
    </html>
  `

      const printWindow = window.open('', '_blank')
      printWindow.document.write(htmlContent)
      printWindow.document.close()
      printWindow.focus()
      printWindow.print()

      showToast({
        title: this.$t('reports.toast.success'),
        message: this.$t('reports.toast.pdf_exported'),
        type: 'success',
      })
    },

    generateSummarySection(summaryData) {
      if (!summaryData) return ''

      return `
        <div class="summary-section">
          <div class="summary-title">${this.$t('billing.summary')}</div>
          <div class="summary-grid">
            <div class="summary-item">
              <span class="summary-label">${this.$t('billing.totalJobs')}</span>
              <span class="summary-value">${summaryData.total_jobs || 0}</span>
            </div>
            <div class="summary-item">
              <span class="summary-label">${this.$t('billing.totalDocuments')}</span>
              <span class="summary-value">${summaryData.total_documents || 0}</span>
            </div>
            <div class="summary-item">
              <span class="summary-label">${this.$t('billing.totalFiles')}</span>
              <span class="summary-value">${summaryData.total_files || 0}</span>
            </div>
            <div class="summary-item">
              <span class="summary-label">${this.$t('billing.totalPages')}</span>
              <span class="summary-value">${summaryData.total_pages || 0}</span>
            </div>
          </div>
          <div class="period-info">${this.$t('billing.period')}: ${summaryData.period || this.generatePeriodFromFilters()}</div>
        </div>
      `
    },

    generatePeriodFromFilters() {
      const { fromDate, toDate } = this.filters
      if (!fromDate && !toDate) return 'All Time'

      if (fromDate && toDate) {
        const fromFormatted = this.formatDateForReport(fromDate)
        const toFormatted = this.formatDateForReport(toDate)
        return `${fromFormatted} - ${toFormatted}`
      }

      if (fromDate) {
        return `From ${this.formatDateForReport(fromDate)}`
      }

      if (toDate) {
        return `Until ${this.formatDateForReport(toDate)}`
      }

      return 'All Time'
    },

    formatDateForReport(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    },

    async downloadAsExcel() {
      // if (!this.writePermission) {
      //   showToast({
      //     title: this.$t('common.no_permission'),
      //     message: this.$t('reports.no_write_permission'),
      //     type: 'error',
      //   })
      //   return
      // }

      this.downloadingExcel = true
      try {
        // Prepare params for fetching all data with export flag
        const params = {
          export: true,
          ...this.filters,
        }

        // Remove empty tenantIds
        if (!params.tenantIds || params.tenantIds.length === 0) {
          delete params.tenantIds
        }

        // Remove empty filter values
        Object.keys(params).forEach((key) => {
          if (params[key] === '' || params[key] === null || params[key] === undefined) {
            delete params[key]
          }
        })

        const queryString = new URLSearchParams(params).toString()

        // Use getBinary to fetch CSV data
        const res = await get(`/v1/reports/billing?${queryString}`)

        // The API returns CSV data directly as text
        let csvData = ''
        if (res?.data) {
          // If the response is already a string (CSV), use it directly
          if (typeof res.data === 'string') {
            csvData = res.data
          } else {
            // If it's an object, try to extract CSV from a property
            csvData = res.data.csv || res.data.data || ''
          }
        }

        if (!csvData || csvData.trim().length === 0) {
          showToast({
            title: this.$t('reports.toast.no_data'),
            message: this.$t('reports.toast.no_billing_data'),
            type: 'warning',
          })
          return
        }

        // Download the CSV data directly
        this.downloadCSV(csvData)
      } catch (error) {
        showToast({
          title: this.$t('reports.toast.error'),
          message: error.response?.data?.message || this.$t('reports.toast.failed_download'),
          type: 'error',
        })
      } finally {
        this.downloadingExcel = false
      }
    },

    downloadCSV(csvData) {
      // Create and download the file
      const blob = new Blob(['\uFEFF' + csvData], {
        type: 'text/csv;charset=utf-8;',
      })

      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)

      // Generate filename with current date and applied filters info
      const currentDate = new Date().toISOString().split('T')[0]
      const filterSuffix = this.getFilterSuffix()
      link.setAttribute('download', `billing_report_${currentDate}${filterSuffix}.csv`)

      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      // Clean up the URL object
      URL.revokeObjectURL(url)

      showToast({
        title: this.$t('reports.toast.success'),
        message: this.$t('reports.toast.excel_exported'),
        type: 'success',
      })
    },

    getFilterSuffix() {
      const activeFilters = []

      if (this.filters.fromDate) activeFilters.push(`from_${this.filters.fromDate}`)
      if (this.filters.toDate) activeFilters.push(`to_${this.filters.toDate}`)
      if (this.filters.tenantIds && this.filters.tenantIds.length > 0) {
        activeFilters.push(`tenant_${this.filters.tenantIds.join('_')}`)
      }

      return activeFilters.length > 0 ? `_${activeFilters.join('_')}` : ''
    },
  },
}
</script>

<style scoped>
@import '@/styles/jobHistory.css';

/* Permission Denied Styles */
.permission-denied {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background-color: #f8f9fa;
}

.permission-denied-content {
  text-align: center;
  padding: 40px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  max-width: 500px;
}

.permission-denied-content h2 {
  color: #dc3545;
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 16px;
}

.permission-denied-content p {
  color: #666;
  font-size: 16px;
  line-height: 1.5;
}

* {
  box-sizing: border-box;
}

.billingReportContainer {
  width: 100%;
  max-width: 100vw;
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
}

.billingContent {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  max-width: 100%;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.billingFiltersContainer {
  flex-shrink: 0;
  margin: 5px 0 15px 0;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  max-width: 100%;
}

.filterRow {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  align-items: flex-end;
}

.filterRow:last-child {
  margin-bottom: 0;
}

.filterGroup {
  flex: 1;
  min-width: 0;
  max-width: 100%;
}

.filterButtonGroup {
  display: flex;
  gap: 1rem;
  flex-shrink: 0;
  min-width: auto;
  flex: none;
}

.filterLabel {
  display: block;
  margin-bottom: 6px;
  color: #344054;
  font-family: Lato, sans-serif;
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.25rem;
}

.filterInput {
  width: 100%;
  height: 2.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  border: 1px solid #d0d5dd;
  background: #fff;
  font-family: Lato, sans-serif;
  font-size: 0.875rem;
  color: #344054;
  transition: border-color 0.2s;
}

.filterInput:focus {
  outline: none;
  border-color: #7ac4eb;
  box-shadow: 0 0 0 2px rgba(122, 196, 235, 0.2);
}



.resetDateButton {
  height: 2.5rem;
  padding: 0 1.5rem;
  border-radius: 6px;
  border: none;
  background: var(--primary-color);
  color: #fff;
  font-family: Lato, sans-serif;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
  margin-top: 1.5625rem;
  white-space: nowrap;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.resetDateButton:hover {
  background: var(--primary-color);
  box-shadow: 0 4px 8px rgba(22, 72, 99, 0.3);
}

.summaryContainer {
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  padding: 1.5rem;
  margin-bottom: 1rem;
  overflow-x: auto !important;
  width: 100% !important;
  display: block !important;
}

.summaryTitle {
  font-family: Lato, sans-serif;
  font-size: 1.125rem;
  font-weight: 600;
  color: #164863;
  margin-bottom: 1rem;
  text-align: center;
}

.summaryGrid {
  display: flex !important;
  flex-wrap: nowrap !important;
  justify-content: space-between !important;
  gap: 1rem !important;
  margin-bottom: 1rem;
  min-width: 800px !important; /* Ensure minimum width for proper layout */
  width: 100%;
  max-width: none;
}

/* Force horizontal layout with maximum specificity */
.billingReportContainer .summaryContainer .summaryGrid {
  display: flex !important;
  flex-direction: row !important;
  flex-wrap: nowrap !important;
  justify-content: space-between !important;
  align-items: center !important;
  width: 100% !important;
  min-width: 800px !important;
  gap: 1rem !important;
  /* Debug styles - visible borders to track layout */
}

.billingReportContainer .summaryContainer .summaryGrid .summaryItem {
  flex: 1 !important;
  display: flex !important;
  flex-direction: column !important;
  align-items: center !important;
  justify-content: center !important;
  min-width: 120px !important;
  /* Debug styles - visible borders to track layout */
}

.summaryItem {
  flex: 1 !important;
  text-align: center;
  padding: 1rem;
  background: #fff;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  min-width: 120px !important;
  display: flex !important;
  flex-direction: column !important;
  align-items: center !important;
  justify-content: center !important;
}

.summaryLabel {
  display: block;
  font-family: Lato, sans-serif;
  font-size: 0.75rem;
  color: #667085;
  margin-bottom: 0.5rem;
  font-weight: 500;
}

.summaryValue {
  display: block;
  font-family: Lato, sans-serif;
  font-size: 1.5rem;
  font-weight: 700;
  color: #164863;
}

.periodInfo {
  text-align: center;
  margin-top: 1rem;
}

.periodLabel {
  font-family: Lato, sans-serif;
  font-size: 0.875rem;
  color: #667085;
  font-weight: 600;
}

.pageOfTitle {
  font-family: Lato, sans-serif;
  font-size: 0.875rem;
  color: #667085;
  white-space: nowrap;
}

.pageDiv {
  position: relative;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #ccc;
  padding: 6px 12px;
  border-radius: 4px;
  background: #fff;
}

.word-wrap {
  white-space: normal !important;
  word-wrap: break-word;
  word-break: break-word;
  overflow-wrap: break-word;
  max-width: 200px;
  line-height: 1.4;
}

.downloadExcelButton {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  height: 2rem;
  padding: 0 1.5rem;
  border-radius: 6px;
  border: none;
  background: var(--primary-color);
  color: #fff;
  font-family: Lato, sans-serif;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
  box-shadow: 0 2px 4px rgba(22, 72, 99, 0.2);
  white-space: nowrap;
  flex-shrink: 0;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}



.downloadExcelButton svg {
  width: 1.25rem;
  height: 1.25rem;
  flex-shrink: 0;
}

.tableContainer {
  flex: 1;
  min-height: 0;
  overflow: auto;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  max-width: 100%;
}

.customTable {
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  table-layout: fixed;
  max-width: 100%;
  word-wrap: break-word;
}

.customTableHeader {
  background: #f8f9fa;
  position: sticky;
  top: 0;
  z-index: 10;
}

.customTableHeader th {
  padding: 0.75rem 1rem;
  text-align: left;
  font-family: Lato, sans-serif;
  font-size: 10px;
  font-weight: 600;
  color: #344054;
  border-bottom: 1px solid #e5e7eb;
  white-space: nowrap;
  /* overflow: hidden; */
  /* text-overflow: ellipsis; */
}

tbody {
  background: #fff;
}

.tableRow {
  height: 3.125rem;
  border-bottom: 1px solid #f2f4f7;
}

.tableRow:hover {
  background-color: var(--secondary-color);
}

tbody td {
  padding: 0.75rem 1rem;
  font-family: Lato, sans-serif;
  font-size: 0.875rem;
  color: #667085;
  vertical-align: top;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.col-tenant-id {
  width: 8%;
}

.col-tenant-name {
  width: 15%;
}

.col-ai-method {
  width: 18%;
}

.col-jobs-processed {
  width: 10%;
}

.col-documents-processed {
  width: 12%;
}

.col-files-processed {
  width: 10%;
}

.col-pages-processed {
  width: 10%;
}

.col-pages-licensed {
  width: 10%;
}

.col-subscription-date {
  width: 17%;
}

.no-data-row {
  height: 12.5rem;
}

.no-data-message {
  text-align: center;
  color: #9ca3af;
}

.no-data-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
}

.no-data-container h3 {
  font-family: Lato, sans-serif;
  font-size: 1.125rem;
  font-weight: 600;
  color: #6b7280;
  margin: 0;
}

.tableContainer::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.tableContainer::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.tableContainer::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.tableContainer::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.tableContainer::-webkit-scrollbar-corner {
  background: #f1f1f1;
}

/* Responsive breakpoints */
@media (max-width: 1200px) {
  .filterGroup {
    min-width: calc(33.333% - 0.67rem);
  }
  .summaryGrid {
    min-width: 800px !important;
    display: flex !important;
    flex-direction: row !important;
    flex-wrap: nowrap !important;
  }
  .billingReportContainer .summaryContainer .summaryGrid {
    display: flex !important;
    flex-direction: row !important;
    min-width: 800px !important;
  }
}

@media (max-width: 900px) {
  .filterGroup {
    min-width: calc(50% - 0.5rem);
  }
}

@media (max-width: 768px) {
  .customTable {
    table-layout: auto;
  }

  .summaryGrid {
    min-width: 800px !important;
    display: flex !important;
    flex-direction: row !important;
    flex-wrap: nowrap !important;
  }
  .billingReportContainer .summaryContainer .summaryGrid {
    display: flex !important;
    flex-direction: row !important;
    min-width: 800px !important;
  }

  .auditFiltersContainer {
    padding: 0.75rem;
  }

  .filterRow {
    gap: 0.75rem;
  }

  .pageOfTitle {
    display: none;
  }
}

@media (max-width: 600px) {
  .filterGroup {
    min-width: 100%;
  }

  .filterButtonGroup {
    width: 100%;
    justify-content: stretch;
  }

  .filterButtonGroup .resetDateButton {
    flex: 1;
  }

  .summaryGrid {
    min-width: 800px !important;
    display: flex !important;
    flex-direction: row !important;
    flex-wrap: nowrap !important;
  }
  .billingReportContainer .summaryContainer .summaryGrid {
    display: flex !important;
    flex-direction: row !important;
    min-width: 800px !important;
  }
}

@media (max-width: 480px) {
  .billingFiltersContainer {
    padding: 0.5rem;
  }

  .customTableHeader th {
    padding: 0.5rem 0.75rem;
    font-size: 0.75rem;
  }

  tbody td {
    padding: 0.5rem 0.75rem;
    font-size: 0.75rem;
  }
}

/* Custom Multi-Select Styles */
.custom-multi-select {
  position: relative;
  width: 100%;
}

.multi-select-input {
  width: 100%;
  min-height: 2.5rem;
  padding: 0.5rem 2.5rem 0.5rem 0.75rem;
  border-radius: 6px;
  border: 1px solid #d0d5dd;
  background: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: border-color 0.2s;
  box-sizing: border-box;
}

.multi-select-input:hover {
  border-color: #7ac4eb;
}

.multi-select-input.open {
  border-color: #7ac4eb;
  box-shadow: 0 0 0 2px rgba(122, 196, 235, 0.2);
}

.selected-tenants {
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  align-items: center;
  min-height: 1.5rem;
}

.placeholder {
  color: black;
  font-family: Lato, sans-serif;
  font-size: 0.875rem;
  background-color: transparent;
}

.tenant-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  align-items: center;
}

.tenant-tag {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.125rem 0.5rem;
  background: #e9f6fd;
  color: #164863;
  border-radius: 4px;
  font-family: Lato, sans-serif;
  font-size: 0.75rem;
  font-weight: 500;
}

.remove-tenant {
  background: none;
  border: none;
  color: #164863;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 2px;
  transition: background-color 0.2s;
}

.remove-tenant:hover {
  background: rgba(22, 72, 99, 0.1);
}

.dropdown-arrow {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  transition: transform 0.2s;
  pointer-events: none;
}

.dropdown-arrow.rotated {
  transform: translateY(-50%) rotate(180deg);
}

.tenant-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1000;
  background: #fff;
  border: 1px solid #d0d5dd;
  border-radius: 6px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  margin-top: 4px;
  max-height: 200px;
  overflow: hidden;
}

.search-container {
  padding: 0.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.search-input {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #d0d5dd;
  border-radius: 4px;
  font-family: Lato, sans-serif;
  font-size: 0.875rem;
  color: #344054;
}

.search-input:focus {
  outline: none;
  border-color: #7ac4eb;
  box-shadow: 0 0 0 2px rgba(122, 196, 235, 0.2);
}

.tenant-options {
  max-height: 150px;
  overflow-y: auto;
}

.tenant-option {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  transition: background-color 0.2s;
  font-family: Lato, sans-serif;
  font-size: 0.875rem;
  color: #344054;
}

.tenant-option:hover {
  background: #f8f9fa;
}

.tenant-option.selected {
  background: #e9f6fd;
  color: #164863;
}

.option-checkbox {
  width: 12px;
  height: 12px;
  border: 1px solid #d0d5dd;
  border-radius: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.tenant-option.selected .option-checkbox {
  background: #164863;
  border-color: #164863;
  color: #fff;
}

.tenant-name {
  flex: 1;
}

.no-tenants {
  padding: 0.75rem;
  text-align: center;
  color: #9ca3af;
  font-family: Lato, sans-serif;
  font-size: 0.875rem;
}

/* Scrollbar styling */
.tenant-options::-webkit-scrollbar {
  width: 6px;
}

.tenant-options::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.tenant-options::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.tenant-options::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>
